import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  viewChildren,
  input,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MercerDataService } from '@coin/shared/master-data/data-access';
import { TinyHelpers } from '@coin/shared/core/util-helpers';
import { MercerData } from '@coin/shared/core/util-models';
import { BehaviorSubject, defer, map, Observable, repeat, Subject } from 'rxjs';
import { JOB_PROFILE_CONFIG } from '../../../mercer-shared/config/job-profile.config';
import { MercerJobType } from '../../../mercer/enums';
import { MercerJobProfile } from '../../../mercer/models/mercer-job-profile.model';
import { MercerDialogType } from '../../enums/mercer-dialog-type.enum';
import { MercerSupportHelper } from '../../helpers/mercer-support.helper';
import { MercerEvaluationMemberDetails } from '../../models/mercer-support-evaluation-member.model';
import { MercerSupportDetailsService } from '../../services/mercer-support-details.service';
import { PositionJobCodeCardComponent } from '../position-job-code-card/position-job-code-card.component';
import { PermissionsService } from '@coin/shared/auth/feature';
import { MercerState } from '../../../mercer/state/mercer.state';
import { MercerSupportState } from '../../../mercer-support/state/mercer-support.state';

@Component({
  selector: 'customer-position-job-code-list',
  templateUrl: './position-job-code-list.component.html',
  styleUrls: ['./position-job-code-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PositionJobCodeListComponent implements OnChanges {
  private mercerDataService = inject(MercerDataService);
  private mercerSupportState = inject(MercerSupportState);
  private mercerState = inject(MercerState);
  mercerSupportDetailsService = inject(MercerSupportDetailsService);
  private permissionsService = inject(PermissionsService);

  public readonly memberDetails = input.required<MercerEvaluationMemberDetails>();
  readonly isPositionClassHidden = input<boolean>(undefined);
  readonly hideSurveyResult = input<boolean>(undefined);
  public readonly derivedPositionClass = input<number>(undefined);
  @Input({ required: true }) public currentMercerData: MercerData;
  readonly dialogType = input.required<MercerDialogType>();
  readonly isFinalizationAllowed = input<boolean>(undefined);
  readonly isQuestionnaireDisabledForGermanWithPartialEvaluation = input<boolean>(undefined);
  readonly hideSavePcButton = input(false);
  readonly disablePositionClass = input(false);
  readonly isLocked = input<boolean>(undefined);
  readonly positionClassValidationErrors = input<string[]>([]);
  readonly positionClassValidationWarnings = input<string[]>([]);

  @Output() public setSurveyTab = new EventEmitter<void>();
  @Output() public selectJobCode = new EventEmitter<string>();
  @Output() public finalizePositionClass = new EventEmitter<void>();
  @Output() public acceptProposedPositionClass = new EventEmitter<void>();
  @Output() public changePositionClass = new EventEmitter<number>();
  @Output() public mercerDataChanged = new EventEmitter<void>();

  selectedJobCode: string;

  mercerSupportJobCodeCardComponents = viewChildren(PositionJobCodeCardComponent);

  public mercerDataChanged$ = new Subject<void>();
  public fetchingData$ = new BehaviorSubject<boolean>(false);
  public jobProfilesTop: MercerJobProfile[];
  public jobProfilesBottom: MercerJobProfile[];
  public mercerJobTypeEnum = MercerJobType;
  public jobCodeLazyLoadFn = (search: string): Observable<string[]> =>
    defer(() => this.mercerDataService.getNonPartialMercerFieldValues(0, 50, 'jobCode', null, search)).pipe(
      repeat({ delay: () => this.mercerDataChanged$ }),
      map(response => response.content)
    );

  // pure fn
  public areStepsCompleted(types: MercerJobType[], jobProfiles: MercerJobProfile[], mercerData: MercerData): boolean {
    const correlatingMercerDataTitleKeys = jobProfiles
      ?.filter(profile => types.includes(profile.type))
      ?.map(profile => profile.correlatingMercerDataTitleKey);
    return correlatingMercerDataTitleKeys?.every(key => !!mercerData?.[key]);
  }

  constructor() {
    this.mercerDataChanged$.pipe(takeUntilDestroyed()).subscribe(() => this.mercerDataChanged.emit());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.memberDetails?.currentValue && !!this.memberDetails()) {
      this.setupJobProfiles();
    }
    if (changes.currentMercerData || changes.memberDetails) {
      this.fetchingData$.next(false);
    }
    if (changes.currentMercerData?.previousValue?.jobCode !== changes.currentMercerData?.currentValue?.jobCode) {
      this.selectedJobCode = changes.currentMercerData?.currentValue?.jobCode;
    }
  }

  public get isSurveyResultVisible(): boolean {
    return (
      this.memberDetails() &&
      !this.hideSurveyResult() &&
      (!this.isQuestionnaireDisabledForGermanWithPartialEvaluation() ||
        MercerSupportHelper.isCandBUserOrAdmin(this.permissionsService))
    );
  }

  public get isSurveyEditable(): boolean {
    return (
      MercerSupportHelper.hasSpecialization(this.currentMercerData) &&
      MercerSupportHelper.arePositionSpecsEditable(
        this.dialogType(),
        this.memberDetails(),
        this.mercerState,
        this.mercerSupportState,
        this.permissionsService
      )
    );
  }

  public get arePositionSpecsEditable(): boolean {
    return MercerSupportHelper.arePositionSpecsEditable(
      this.dialogType(),
      this.memberDetails(),
      this.mercerState,
      this.mercerSupportState,
      this.permissionsService
    );
  }

  public get isValidationEditable(): boolean {
    return MercerSupportHelper.isValidationEditable(
      this.memberDetails(),
      this.mercerState,
      this.mercerSupportState,
      this.dialogType(),
      this.permissionsService
    );
  }

  public trackByJobProfileItem(index: number, profile: MercerJobProfile): string | number {
    return profile?.correlatingMercerDataTitleKey || index;
  }

  public setupJobProfiles(): void {
    const jobProfiles = structuredClone(JOB_PROFILE_CONFIG);
    this.jobProfilesTop = this.mapJobProfiles(jobProfiles, [
      MercerJobType.Family,
      MercerJobType.SubFamily,
      MercerJobType.Specialization
    ]);
    this.jobProfilesBottom = this.isSurveyResultVisible
      ? this.mapJobProfiles(jobProfiles, [MercerJobType.CareerStream, MercerJobType.CareerLevel])
      : [];
  }

  public saveNewJobCode(): void {
    this.selectJobCode.emit(this.selectedJobCode);
  }

  public goToNextProfile(currentProfile: MercerJobProfile): void {
    const currentIndex = JOB_PROFILE_CONFIG.findIndex(profile => profile.type === currentProfile.type);

    if (currentIndex > -1 && currentProfile.type === TinyHelpers.lastItem(this.jobProfilesTop)?.type) {
      this.setSurveyTab.emit();
      return;
    }

    if (currentIndex === -1 || currentIndex === JOB_PROFILE_CONFIG.length - 1) {
      return;
    }

    const nextComponent = this.mercerSupportJobCodeCardComponents().find(
      component => component.profile().type === JOB_PROFILE_CONFIG[currentIndex + 1].type
    );

    if (!nextComponent) {
      return;
    }

    nextComponent.selectedMercerJobType$.next(nextComponent.profile().type);
  }

  public processProfileEditModeChange(currentProfile: MercerJobProfile): void {
    const components = this.mercerSupportJobCodeCardComponents().filter(
      component => component.profile().type !== currentProfile.type
    );
    components.forEach(component => component.selectedMercerJobType$.next(null));
  }

  private mapJobProfiles(jobProfiles: MercerJobProfile[], types: MercerJobType[]): MercerJobProfile[] {
    return jobProfiles?.filter(profile => types.includes(profile.type));
  }
}
